import { Building2, Plus, Loader2, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/Card.js';
import { Button } from '@/components/ui/Button.js';
import { CreateOrgDialog } from '@/components/orgs/CreateOrgDialog.js';
import { WizardChain } from '@/wizards/WizardChain.js';
import { api, type Organisation } from '@/api/client.js';

export function OrgsPage(): JSX.Element {
  const [open, setOpen] = useState(false);
  // Wizard-chain state. When the operator clicks "Continue setup" on
  // the org-creation success view we close the org dialog and start
  // the chain. The chain is wired through the wizard-hook registry
  // (see `wizards/register.ts`) so any future plugin's `org.created`
  // hook fires after the application hook without changes here.
  const [chainOrg, setChainOrg] = useState<Organisation | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['organisations'],
    queryFn: () => api.organisations.list(),
  });

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Organisations</h1>
          <p className="text-sm text-muted-foreground">
            Tree-shaped. New child orgs come with an admin invitation that creates the admin user on
            accept.
          </p>
        </div>
        <Button onClick={() => setOpen(true)}>
          <Plus className="h-4 w-4" />
          New organisation
        </Button>
      </header>

      <Card>
        <CardContent className="py-2">
          {isLoading ? (
            <div className="flex justify-center py-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : isError || !data ? (
            <div className="py-8 text-center text-sm text-destructive">Could not load organisations.</div>
          ) : data.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              You don't belong to any organisations yet.
            </div>
          ) : (
            <OrgTree orgs={data} />
          )}
        </CardContent>
      </Card>

      <CreateOrgDialog
        open={open}
        onOpenChange={setOpen}
        parents={data ?? []}
        onCreated={() => {
          // Refresh the tree once the new org lands.
          void queryClient.invalidateQueries({ queryKey: ['organisations'] });
        }}
        onCreateApplicationAfter={(org) => {
          // CreateOrgDialog already closed itself before calling us.
          // Hand the org to the chain controller; the registered
          // `org.created` hooks fire in order (application setup
          // first, then any backup / monitoring / etc. hooks that
          // future plugins register).
          setChainOrg(org);
        }}
      />

      <WizardChain
        eventKind="org.created"
        event={chainOrg ? { org: chainOrg } : null}
        isActive={chainOrg !== null}
        onAllDone={() => setChainOrg(null)}
      />
    </div>
  );
}

function OrgTree({ orgs }: { orgs: Organisation[] }): JSX.Element {
  // Build a parent → children map (orgs may have parentOrgId null for roots).
  const byParent = new Map<string | null, Organisation[]>();
  for (const o of orgs) {
    const k = o.parentOrgId;
    if (!byParent.has(k)) byParent.set(k, []);
    byParent.get(k)!.push(o);
  }
  // Roots = orgs whose parent isn't itself in the list (could be top-level OR a parent the user can't see).
  const visibleIds = new Set(orgs.map((o) => o.id));
  const roots = orgs.filter((o) => o.parentOrgId === null || !visibleIds.has(o.parentOrgId));

  return (
    <ul className="divide-y divide-border">
      {roots.map((root) => (
        <OrgRow key={root.id} org={root} depth={0} byParent={byParent} />
      ))}
    </ul>
  );
}

function OrgRow({
  org,
  depth,
  byParent,
}: {
  org: Organisation;
  depth: number;
  byParent: Map<string | null, Organisation[]>;
}): JSX.Element {
  const children = byParent.get(org.id) ?? [];
  return (
    <>
      <li>
        <Link
          to="/orgs/$slug"
          params={{ slug: org.slug }}
          className="flex items-center gap-3 rounded-md px-2 py-3 hover:bg-accent/40"
          style={{ paddingLeft: `${depth * 24 + 8}px` }}
        >
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <div className="flex-1">
            <div className="text-sm font-medium">{org.name}</div>
            <div className="text-xs text-muted-foreground">
              <span className="font-mono">/{org.slug}</span>
            </div>
          </div>
          {depth === 0 && (
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-primary">
              root
            </span>
          )}
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </Link>
      </li>
      {children.map((child) => (
        <OrgRow key={child.id} org={child} depth={depth + 1} byParent={byParent} />
      ))}
    </>
  );
}
